import React, { Component } from "react";
import PropTypes from "prop-types";

import classnames from "classnames";
import queryString from "query-string";

import {
	EXISTING_PAYMENT_SCREEN,
	PAYPAL_SCREEN,
	CREDIT_FORM_NAMES,
	PRODUCT_SUBTYPES,
	SAVE_PAYPAL_INFO_PRODUCT_ID,
	QUERY_PARAM_PAYMENT_TYPE,
	PAYMENT_TYPES_TO_SCREEN,
} from "shared/payment/constants";

import Screen from "shared/payment/components/Screen";
import WindowShade from "shared/payment/components/WindowShade";

import NewInfo from "apps/payment/components/NewInfo";
import ExistingInfo from "apps/payment/components/ExistingInfo";

import OkLoader from "shared/components/OkLoader";
import Context from "shared/util/Context";

import "./Payment.scss";

// map okpay productids to new subtypes
const OKPAY_PRODUCTIDS_TO_SUBTYPE = {
	spotlight: PRODUCT_SUBTYPES.BOOST,
	alist: PRODUCT_SUBTYPES.ALIST,
	incognito: PRODUCT_SUBTYPES.INCOGNITO,
};


function getQuery() {
	if (!window || !window.location || !window.location.search) {
		return {};
	}

	return queryString.parse(window.location.search);
}

export class Payment extends Component {
	static propTypes = {
		isEditingInfo: PropTypes.bool.isRequired,
		isVisible: PropTypes.bool.isRequired,
		currentScreen: PropTypes.string.isRequired,
		isSuccessful: PropTypes.bool.isRequired,
		isPurchasing: PropTypes.bool.isRequired,
		isLoading: PropTypes.bool.isRequired,
		product: PropTypes.object,
		productChoices: PropTypes.array,

		paymentToken: PropTypes.shape({
			tokenId: PropTypes.string,
			paymentProcessor: PropTypes.string,
		}).isRequired,
		userInfo: PropTypes.shape({
			state: PropTypes.string,
		}).isRequired,

		needsUserState: PropTypes.bool.isRequired,

		close: PropTypes.func.isRequired,
		setProduct: PropTypes.func.isRequired,
		navigateScreen: PropTypes.func.isRequired,
		getUserState: PropTypes.func.isRequired,
		confirmPurchaseStoredInfo: PropTypes.func.isRequired,

		fetchProductId: PropTypes.func.isRequired,
		confirmPurchasePaypal: PropTypes.func.isRequired,
		setErrorMessage: PropTypes.func.isRequired,
		savePaypalInfo: PropTypes.func.isRequired,
		fetchPaypalLink: PropTypes.func.isRequired,
	};

	static defaultProps = {
		product: null,
		productChoices: [],
	}

	componentDidMount() {
		const query = getQuery();

		if (!query) {
			return;
		}

		// componet did mount is called here when:
		// 1. the modal save billing/ purchase modal is opened
		// 2. paypal redirects the user back to our site after they link their account/ buy product

		if (query.paypalsuccess || query.paypalcancel) {
			if (query.productid === SAVE_PAYPAL_INFO_PRODUCT_ID) {
				this.props.savePaypalInfo(query);
			} else {
				// just fetch for now; once we get the product, process the transaction
				this.props.fetchProductId({
					productid: query.productid,
					productSubtype: query.type,
					country: query.country,
				});

				// if by some chance that instantly gets a product, fucking jam
				if (this.props.product) {
					this._confirmPaypalPurchase();
				}
			}

			return;
		}

		const paymentType = QUERY_PARAM_PAYMENT_TYPE[query.paymentType];

		if (paymentType) {
			this.props.navigateScreen(PAYMENT_TYPES_TO_SCREEN[paymentType]);
		}

		if (query.productid && query.type) {
			// if not paypal success, it's mobile
			const country = Context.get("country");
			const { productid, type } = query;
			this.props.fetchProductId({
				productid,
				country,
				productSubtype: type,
			});
		}
		else if (process.env.PLATFORM === "mobile" &&
			OKPAY_PRODUCTIDS_TO_SUBTYPE[query.product]) {
			// old mw links that just link to a single boost
			// Android pay by credit card uses this too
			const country = Context.get("country");

			let productSubtype = OKPAY_PRODUCTIDS_TO_SUBTYPE[query.product];

			// correct product subtype coming from android; it always comes in as alist
			if (productSubtype === PRODUCT_SUBTYPES.ALIST && query.premium) {
				productSubtype = PRODUCT_SUBTYPES.ALISTPREMIUM;
			}

			this.props.fetchProductId({
				country,
				productSubtype,
				tokensNumber: query.tokensNumber,
				subPeriod: query.subPeriod,
			});
		}
	}

	componentWillReceiveProps(nextProps) {
		if (!this.props.isVisible && nextProps.isVisible && nextProps.needsUserState) {
			nextProps.getUserState();
		}

		// process paypal payment after the product actually gets loaded
		if (!this.props.product && nextProps.product) {
			this._confirmPaypalPurchase();
		}
	}

	_confirmPaypalPurchase = () => {
		const query = getQuery();

		if (query.productid) {
			if (query.paypalsuccess) {
				this.props.confirmPurchasePaypal(query);
			}

			if (query.paypalcancel) {
				this.props.setErrorMessage({
					name: CREDIT_FORM_NAMES.paypalPurchaseScreen,
					errorMessage: "Oops, the PayPal transaction didn’t go through!",
				});
			}
		}
	};

	_setProduct = (product) => {
		this.props.setProduct(product);

		if (this.props.currentScreen === PAYPAL_SCREEN) {
			this.props.fetchPaypalLink();
		}
	};

	_getScreenInternals = () => {
		if (this.props.isLoading || this.props.needsUserState) {
			return <OkLoader show />;
		}

		const {
			productChoices,
			isEditingInfo,
			currentScreen,
			product,

			userInfo,
			navigateScreen,
			close,
			paymentToken,
			confirmPurchaseStoredInfo,
			isSuccessful,
			isPurchasing,
		} = this.props;

		const isExistingScreen = currentScreen === EXISTING_PAYMENT_SCREEN;
		const isPaypalScreen = currentScreen === PAYPAL_SCREEN;

		return (
			<span>
				{process.env.PLATFORM === "desktop" &&
					<span
						role="button"
						tabIndex={0}
						className={classnames({
							"okicon": true,
							"i-close": true,
							"payment-close": true,
							"payment-close--existing": isExistingScreen,
						})}
						onClick={close}
					/>
				}

				{isExistingScreen && paymentToken &&
					<ExistingInfo
						paymentToken={paymentToken}
						product={product}
						userInfo={userInfo}
						purchase={confirmPurchaseStoredInfo}
						isSuccessful={isSuccessful}
						isPurchasing={isPurchasing}
						navigateScreen={navigateScreen}
						setProduct={this._setProduct}
						productChoices={productChoices}
					/>
				}

				{!isExistingScreen &&
					<NewInfo
						currentScreen={currentScreen}
						navigateScreen={navigateScreen}
						isPaypalScreen={isPaypalScreen}
						isEditingInfo={isEditingInfo}
						product={product}
						setProduct={this._setProduct}
						productChoices={productChoices}
					/>
				}
			</span>
		);
	}

	render() {
		const { isVisible, close, currentScreen } = this.props;
		const isExistingScreen = currentScreen === EXISTING_PAYMENT_SCREEN;

		const internals = (
			<Screen
				className={classnames({
					"payment": true,
					"payment--existing": isExistingScreen,
					"payment--newinfo": !isExistingScreen,
				})}
			>
				{this._getScreenInternals()}
			</Screen>
		);

		if (process.env.PLATFORM === "mobile") {
			return internals;
		}

		return (
			<WindowShade
				isVisible={isVisible}
				toggleVisible={close}
			>
				{internals}
			</WindowShade>
		);
	}
}

export default Payment;



// WEBPACK FOOTER //
// ./src/apps/payment/components/Payment/index.jsx